import { useAtom } from "jotai";
import { useCallback, useEffect, useState } from "react";
import { useErrorHandler } from "react-error-boundary";
import ReactInputMask from "react-input-mask";

import { updateFormioSubmission } from "../../../../shared/api/yenta-api";
import { validatePhoneNumber } from "../../../../shared/utils";
import {
  currentQuestionIndexAtom,
  landerQuizAtom,
  landerQuizQuestionAtom,
  showLoadingSpinnerAtom,
} from "../../../lander-quiz.state";
import { LanderQuizQuestion } from "../../../lander-quiz.types";
import {
  grantLanderQuizExtraEntries,
  trackLanderQuizVariable,
} from "../../../lander-quiz.utils";
import { QuestionButton } from "../../shared/question-button/question-button.component";
import { QuestionTitle } from "../../shared/question-title/question-title.component";

export const SmsQuestion: React.FC = () => {
  const [landerQuiz] = useAtom(landerQuizAtom);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useAtom(
    currentQuestionIndexAtom
  );
  const [currentQuestion, setCurrentQuestion] = useAtom(
    landerQuizQuestionAtom(currentQuestionIndex)
  );
  const [, setShowLoadingSpinner] = useAtom(showLoadingSpinnerAtom);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneNumberError, setPhoneNumberError] = useState(false);
  const [tacAccepted, setTacAccepted] = useState(false);
  const [showFeedbackMessages, setShowFeedbackMessages] = useState(false);
  const handleWidgetError = useErrorHandler();

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setPhoneNumber(e.target.value);
    },
    []
  );

  const handleTACToggle = useCallback(() => {
    setTacAccepted(!tacAccepted);
  }, [tacAccepted]);

  const saveSmsDataToFormio = async () => {
    const submission = window.theme.Sweepstake.prototype.retrieveSubmission();

    if (!submission) {
      return;
    }

    const sweepFormUrl =
      document
        .querySelector("#FormSweepstake")
        ?.getAttribute("data-form-path-url") || "";

    const patchData = {
      ...submission.data,
      ...{
        smsMobilePhone: phoneNumber,
        smsTermsAndConditions: true,
      },
    };

    await updateFormioSubmission(
      sweepFormUrl,
      {
        ...submission,
        data: patchData,
      },
      handleWidgetError
    ).then((submission) => {
      window.theme.Sweepstake.prototype.storeSubmission(submission);
      window.theme.Sweepstake.prototype.storeSubmissionHash(submission);
    });
  };

  const continueBtnFn = async (smsAccepted: boolean) => {
    if (smsAccepted && (phoneNumberError || !tacAccepted)) {
      setShowFeedbackMessages(true);
      return;
    }

    const answer = smsAccepted ? phoneNumber : "no thanks";
    const updatedQuestion: LanderQuizQuestion = {
      ...currentQuestion,
      answer,
    };
    const isLastQuestion =
      currentQuestionIndex + 1 >= landerQuiz.questions.length;

    setCurrentQuestion(updatedQuestion);
    trackLanderQuizVariable(updatedQuestion);

    const isNonProfitSweep = window.FKL.isNonProfitSweep;
    const isSmsFirstQuestion = currentQuestionIndex === 0;

    if (isNonProfitSweep && !smsAccepted && isSmsFirstQuestion) {
      setShowLoadingSpinner(true);
      window.theme.LanderQuizUtils.handleNextSweepFlowStep();
      return;
    }

    if (!isLastQuestion) {
      smsAccepted && (await saveSmsDataToFormio());
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      return;
    }

    setShowLoadingSpinner(true);

    await grantLanderQuizExtraEntries(
      { phoneNumber: phoneNumber, smsAccepted: smsAccepted },
      handleWidgetError
    );

    window.theme.LanderQuizUtils.saveDemographicQuizCompletionInSession();
    window.theme.LanderQuizUtils.handleNextSweepFlowStep();
  };

  useEffect(() => {
    const validPhoneNumber = validatePhoneNumber(phoneNumber);
    validPhoneNumber && setTacAccepted(true);
    setPhoneNumberError(!validPhoneNumber);
  }, [phoneNumber]);

  return (
    <div className="sms-question">
      <QuestionTitle title={currentQuestion.title} />
      <div className="sms-question__input-container">
        <ReactInputMask
          className="sms-question__input"
          mask="(999) 999-9999"
          placeholder="Phone number..."
          value={phoneNumber}
          onChange={handleInputChange}
        />
        {showFeedbackMessages && phoneNumberError ? (
          <div className="sms-question__input__feedback--incorrect">
            Please enter a valid phone number
          </div>
        ) : null}
      </div>
      <label className="quiz__label">
        <input
          disabled={phoneNumberError}
          type="checkbox"
          onChange={handleTACToggle}
          checked={tacAccepted}
        />
        <div className="sms-question__tac">
          I agree to the Terms &amp; Conditions of this sweepstake. I agree to
          receive SMS communications from FindKeep.Love.
        </div>
      </label>
      {showFeedbackMessages && !phoneNumberError && !tacAccepted ? (
        <div className="sms-question__input__feedback--incorrect">
          Please accept the Terms &amp; Conditions
        </div>
      ) : null}
      <div className="buttons-question__options-container">
        <QuestionButton
          key={`option-${currentQuestionIndex}-true`}
          label={"Sign me up!"}
          isSelected={!phoneNumberError && tacAccepted}
          isDisabled={false}
          clickFn={() => continueBtnFn(true)}
        />
        <QuestionButton
          key={`option-${currentQuestionIndex}-false`}
          label={"No thanks"}
          isSelected={false}
          isDisabled={false}
          clickFn={() => continueBtnFn(false)}
        />
      </div>
      <div className="sms-question__disclaimer">
        {
          "FindKeepLove will never call you, but we'll send you giveaway announcement text messages if you're interested."
        }
      </div>
    </div>
  );
};
